/**
 * Ordre alphabétique des personnes — une seule définition pour tout l'écran.
 *
 * Jumeau de `backend/src/lib/tri-personnes.js`. Il existe des deux côtés parce
 * que certaines listes sont construites dans le navigateur (filtres, matrices,
 * listes fusionnées) et ne repassent jamais par le serveur.
 *
 * Pourquoi les deux côtés trient : le serveur ordonne ce qu'il renvoie (c'est
 * lui qui alimente aussi les exports Excel et les PDF), l'écran ordonne ce
 * qu'il assemble lui-même (matrice de présence, listes fusionnées, sélections).
 * Une liste qui arrive déjà triée et qu'on ne fait que filtrer n'est PAS
 * re-triée : `filter` garde l'ordre.
 *
 * `localeCompare` sans argument suit la langue du NAVIGATEUR : le même écran se
 * rangeait autrement selon le poste. Ici la langue est fixée à « fr », accents
 * rangés près de la lettre nue et casse ignorée, comme dans un annuaire.
 */

const collator = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });

/** Le libellé réellement affiché — c'est sur lui qu'on trie, pas sur un champ caché. */
function etiquette(p) {
  if (p == null) return '';
  if (typeof p === 'string') return p;
  return p.name ?? p.userName ?? p.fullName ?? p.label ?? p.username ?? '';
}

/** Départages (username, puis id) : deux homonymes ne changent plus de place. */
export function comparerPersonnes(a, b) {
  const parNom = collator.compare(etiquette(a), etiquette(b));
  if (parNom !== 0) return parNom;
  const parIdentifiant = collator.compare(a?.username ?? '', b?.username ?? '');
  if (parIdentifiant !== 0) return parIdentifiant;
  return collator.compare(String(a?.id ?? a?.userId ?? ''), String(b?.id ?? b?.userId ?? ''));
}

/** Renvoie une COPIE triée : trier sur place casserait les listes mémorisées. */
export function trierPersonnes(liste) {
  if (!Array.isArray(liste)) return liste;
  return [...liste].sort(comparerPersonnes);
}

/**
 * Suffixe « (inactif) » pour un compte désactivé.
 *
 * L'ordre étant purement alphabétique, un compte éteint se retrouve au milieu
 * des collègues en poste. Les listes où l'historique compte (congés rétroactifs,
 * erreurs passées, export paie) gardent donc ces comptes, mais les DISENT.
 */
export function marqueurInactif(p) {
  return p && p.isActive === false ? ' (inactif)' : '';
}
